import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _to_property_key } from "@swc/helpers/_/_to_property_key";
/**
 * Zustand client-state store for the Drafts AI Search feature.
 *
 * Holds the transient UI state of an in-progress AI search: the structured
 * `DraftsSearchFilter` extracted from the user's query, the original query text,
 * submission/abort status, whether the filter was edited since the last search,
 * and the latest error code. It also exposes per-field actions to edit, add,
 * remove, and restructure (AND/OR) predicates within the filter.
 *
 * Server data (the actual search results) is NOT stored here — that lives in
 * react-query (see `useDraftsAiSearchResults`).
 */ import { create } from 'zustand';
import { withNetTerms } from '../settings-predicates';
const isPredicateEmpty = (predicate)=>Object.values(predicate).every((value)=>value == null || Array.isArray(value) && value.length === 0);
const isFilterEmpty = (filter)=>{
    for (const [key, value] of Object.entries(filter)){
        if (key === 'version') continue;
        if (value != null) return false;
    }
    return true;
};
const updatePredicate = (state, key, value)=>{
    if (state.filter == null) return {};
    const _state_filter = state.filter, { [key]: _omitted } = _state_filter, rest = _object_without_properties(_state_filter, [
        _to_property_key(key)
    ]);
    const nextFilter = value == null ? _object_spread_props(_object_spread({}, rest), {
        version: state.filter.version
    }) : _object_spread_props(_object_spread({}, rest), {
        version: state.filter.version,
        [key]: value
    });
    return isFilterEmpty(nextFilter) ? {
        filter: null,
        lastQuery: ''
    } : {
        filter: nextFilter
    };
};
const markFilterEdited = (update)=>_object_spread_props(_object_spread({}, update), {
        filterEditedSinceLastSearch: true
    });
const omitFilterKey = (filter, key)=>{
    const rest = Object.fromEntries(Object.entries(filter).filter((param)=>{
        let [k] = param;
        return k !== key && k !== 'version';
    }));
    // WHY: `key` is a dynamic runtime string (an unrecognized filter field), so a typed
    // rest-omit can't be expressed against DraftsSearchFilter — its known predicate fields
    // would all widen to `unknown`. This is the one honest cast for that case; `version` is
    // carried through explicitly so dropping the required literal would still be a type error.
    return _object_spread_props(_object_spread({}, rest), {
        version: filter.version
    });
};
const collapseGroupToArm = (filter, key, arm)=>{
    const { [key]: _omittedGroup } = filter, rest = _object_without_properties(filter, [
        _to_property_key(key)
    ]);
    const collapsed = _object_spread({}, rest, arm);
    return isFilterEmpty(collapsed) ? {
        filter: null,
        lastQuery: ''
    } : {
        filter: collapsed
    };
};
export const useDraftsAiSearchStore = create((set)=>({
        filter: null,
        lastQuery: '',
        isSubmitting: false,
        filterEditedSinceLastSearch: false,
        errorCode: null,
        abortInFlight: null,
        setSubmitting: (submitting)=>set({
                isSubmitting: submitting
            }),
        setAbortInFlight: (abort)=>set({
                abortInFlight: abort
            }),
        setFilter: (filter, lastQuery)=>set({
                filter,
                lastQuery,
                errorCode: null,
                filterEditedSinceLastSearch: false
            }),
        clearFilter: ()=>{
            const abort = useDraftsAiSearchStore.getState().abortInFlight;
            abort === null || abort === void 0 ? void 0 : abort();
            set({
                filter: null,
                lastQuery: '',
                errorCode: null,
                isSubmitting: false,
                abortInFlight: null,
                filterEditedSinceLastSearch: false
            });
        },
        setError: (code)=>set({
                errorCode: code
            }),
        setAgreementName: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'agreementName', predicate))),
        setClientName: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'clientName', predicate))),
        setContactName: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'contactName', predicate))),
        setStatus: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'status', predicate))),
        setEffectiveDate: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'effectiveDate', predicate))),
        setEffectiveDateMode: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'effectiveDateMode', predicate))),
        setLastUpdatedAt: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'lastUpdatedAt', predicate))),
        setLastModifiedBy: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'lastModifiedBy', predicate))),
        setNetTerms: (predicate)=>set((s)=>s.filter == null ? {} : markFilterEdited(updatePredicate(s, 'paymentSettings', withNetTerms(s.filter.paymentSettings, predicate)))),
        setPaymentSettings: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'paymentSettings', predicate))),
        setAgreementSettings: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'agreementSettings', predicate))),
        setServices: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'services', predicate))),
        setPackages: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'packages', predicate))),
        setBundles: (predicate)=>set((s)=>markFilterEdited(updatePredicate(s, 'bundles', predicate))),
        setOrArm: (index, arm)=>set((s)=>{
                var _s_filter_key;
                if (s.filter == null) return {};
                const key = s.filter.or != null ? 'or' : s.filter.and != null ? 'and' : null;
                if (key == null) return {};
                const next = [
                    ...(_s_filter_key = s.filter[key]) !== null && _s_filter_key !== void 0 ? _s_filter_key : []
                ];
                if (arm == null || isPredicateEmpty(arm)) {
                    next.splice(index, 1);
                } else {
                    next[index] = arm;
                }
                if (next.length === 1) {
                    return markFilterEdited(collapseGroupToArm(s.filter, key, next[0]));
                }
                return markFilterEdited(updatePredicate(s, key, next.length === 0 ? undefined : next));
            }),
        toggleConnector: ()=>set((s)=>{
                var _s_filter, _s_filter1;
                if (((_s_filter = s.filter) === null || _s_filter === void 0 ? void 0 : _s_filter.or) != null && s.filter.or.length > 0) {
                    const _s_filter = s.filter, { or } = _s_filter, rest = _object_without_properties(_s_filter, [
                        "or"
                    ]);
                    return markFilterEdited({
                        filter: _object_spread_props(_object_spread({}, rest), {
                            and: or
                        })
                    });
                }
                if (((_s_filter1 = s.filter) === null || _s_filter1 === void 0 ? void 0 : _s_filter1.and) != null && s.filter.and.length > 0) {
                    const _s_filter = s.filter, { and } = _s_filter, rest = _object_without_properties(_s_filter, [
                        "and"
                    ]);
                    return markFilterEdited({
                        filter: _object_spread_props(_object_spread({}, rest), {
                            or: and
                        })
                    });
                }
                return {};
            }),
        clearGroup: ()=>set((s)=>{
                var _s_filter, _s_filter1;
                if (((_s_filter = s.filter) === null || _s_filter === void 0 ? void 0 : _s_filter.or) != null) return markFilterEdited(updatePredicate(s, 'or', undefined));
                if (((_s_filter1 = s.filter) === null || _s_filter1 === void 0 ? void 0 : _s_filter1.and) != null) return markFilterEdited(updatePredicate(s, 'and', undefined));
                return {};
            }),
        removeUnknownField: (key)=>set((s)=>{
                if (s.filter == null || key === 'version') return {};
                const nextFilter = omitFilterKey(s.filter, key);
                return markFilterEdited(isFilterEmpty(nextFilter) ? {
                    filter: null,
                    lastQuery: ''
                } : {
                    filter: nextFilter
                });
            })
    }));
